// Fehler der Anmelde-Routen deuten – EINE Stelle für Anmeldeseite, Profil und Verwaltung. Der Server liefert fertige
// deutsche Sätze (AUTH_TEXTS); hier wird nur entschieden, WOHIN ein Fehler gehört (Feld, Kopf der Karte, zurück zum
// Kennwort) und was ein 401 mit `details.reason` bedeutet (docs/anmeldung/SPEZIFIKATION.md §8.1, §9.3).
import { AUTH_REASONS, AUTH_TEXTS, MFA_LIMITS, zLoginChallenge, type LoginChallenge } from '@shared/auth'
import { isAppError, type AppError } from '@shared/errors'

/**
 * 401 nach richtigem Kennwort: zweiter Faktor verlangt (`mfa_required`) oder Einrichtung Pflicht (`mfa_setup_required`).
 * Fehlen Felder (älterer Server), gilt die Spezifikation: Code oder Wiederherstellungscode, fünf Minuten.
 */
export function loginChallengeOf(error: unknown, now: Date = new Date()): LoginChallenge | null {
  if (!isAppError(error) || error.code !== 'UNAUTHENTICATED') return null
  const details = error.details ?? {}
  const parsed = zLoginChallenge.safeParse(details)
  if (parsed.success) return parsed.data
  const expiresAt = new Date(now.getTime() + MFA_LIMITS.challengeMinutes * 60_000).toISOString()
  if (details.reason === AUTH_REASONS.mfaRequired) return { reason: 'mfa_required', methods: ['totp', 'recovery'], expiresAt }
  if (details.reason === AUTH_REASONS.mfaSetupRequired) return { reason: 'mfa_setup_required', expiresAt }
  return null
}

/** 403 des Dispatchers bzw. einer Konto-Route: Diese Sitzung muss sich frisch bestätigen (Kennwort oder Code). */
export const isReauthRequired = (error: unknown): boolean =>
  isAppError(error) && error.code === 'FORBIDDEN' && error.details?.reason === AUTH_REASONS.reauthRequired

/** Gründe, aus denen der Server eine Challenge für beendet erklärt (verbraucht, abgelaufen, nach Fehlversuchen gesperrt) */
const CHALLENGE_GONE_REASONS: readonly unknown[] = ['challenge_expired', 'challenge_invalid', 'challenge_locked', 'mfa_locked']

/**
 * Nach diesem Fehler gilt die Challenge nicht mehr – die Anmeldung beginnt wieder beim Kennwort: Ablauf, zu viele
 * falsche Codes, Sperre je Person (429) oder ein Satz des Servers, der genau das sagt.
 */
export function isChallengeGone(error: unknown): boolean {
  if (!isAppError(error)) return false
  if (error.code === 'LIMIT_EXCEEDED') return true
  if (error.code !== 'UNAUTHENTICATED') return false
  return (
    CHALLENGE_GONE_REASONS.includes(error.details?.reason) ||
    error.message === AUTH_TEXTS.challengeExpired ||
    error.message === AUTH_TEXTS.codeLocked
  )
}

/** Satz des Servers zu einem Feld (VALIDATION mit details.fields), sonst undefined. */
export const fieldMessage = (error: unknown, path: string): string | undefined =>
  isAppError(error) ? error.details?.fields?.find((field) => field.path === path)?.message : undefined

/** Satz zu einem falschen Code: Feldfehler des Servers, sein Satz – oder der Standardsatz. */
export function codeFailureText(error: unknown): string {
  const field = fieldMessage(error, 'code')
  if (field) return field
  if (isAppError(error) && error.code === 'UNAUTHENTICATED' && error.message && error.message !== 'Bitte melden Sie sich an.') return error.message
  if (isAppError(error) && error.code !== 'UNAUTHENTICATED' && error.code !== 'VALIDATION') return error.message
  return AUTH_TEXTS.codeWrong
}

/** Satz zu einem falschen Kennwort bei der Bestätigung: Die Anmeldung meldet „E-Mail-Adresse oder Kennwort …“ – hier ist die Adresse klar. */
export function passwordFailureText(error: unknown): string {
  const field = fieldMessage(error, 'password') ?? fieldMessage(error, 'current')
  if (field) return field
  if (!isAppError(error)) return 'Das hat nicht geklappt. Bitte versuchen Sie es noch einmal.'
  if (error.code === 'UNAUTHENTICATED' || error.code === 'VALIDATION') {
    return error.message === AUTH_TEXTS.loginFailed || error.message === 'Bitte melden Sie sich an.' || error.code === 'VALIDATION'
      ? 'Das Kennwort stimmt nicht.'
      : error.message
  }
  return error.message
}

/** Wartezeit aus einer Drosselung (429 mit details.retryAfterSeconds) als Satz – sonst der Satz des Servers. */
export function throttleText(error: AppError): string {
  const retryAfter = error.details?.retryAfterSeconds
  if (typeof retryAfter === 'number' && retryAfter > 0) {
    const minutes = Math.ceil(retryAfter / 60)
    return `Zu viele Anmeldeversuche. Bitte versuchen Sie es in ${minutes === 1 ? 'einer Minute' : `${minutes} Minuten`} erneut.`
  }
  return error.message
}
